import { TypedEventEmitter } from '../lib/core'
import { deltas, floodFill, getWeightedCenter, unique } from '../lib/algo'
import type { CompleteGameData, CompleteItemState, Drop, highlightType, instanceId, itemId, Position, tileKey } from '../data/types'
import { reactive } from 'vue'
import { calculateMergeDrops } from '../lib/merge-helpers'
import type { GameEvents, ScheduledEvents } from './events'


interface CompleteGameState {
    tick: number
    items: Map<instanceId, CompleteItemState>
    tiles: Map<tileKey, Tile>
    size: Position
    dragging: {
        from: Position
        over: Position | null
    } | null
    hoveredTile: Tile | null
    highlight: Record<tileKey, highlightType> | null
    scheduledEvents: {
        event: keyof ScheduledEvents
        atTick: number
        detail: ScheduledEvents[keyof ScheduledEvents]
    }[]
    /** Max orb power achieved - determines visibility into shade */
    visibilityThreshold: number
    /** Game over state */
    gameOver: 'playing' | 'victory' | 'defeat'
}

export class Item extends (Object as any as (new () => CompleteItemState)) {
    constructor(itemData: CompleteItemState) {
        super()
        const { data, ...rest } = itemData
        Object.assign(this, rest)
        // Make data non-enumerable so it won't be serialized
        Object.defineProperty(this, 'data', {
            value: data,
            writable: true,
            enumerable: false,
            configurable: true
        })
        return reactive(this)
    }

    static from(saved: any, game: Game): Item {
        const data = game.data.items[saved.id]
        if (!data) throw new Error(`Item data not found for id: ${saved.id}`)
        return new Item({ ...saved, data })
    }
}

export class Tile {
    position!: Position
    itemUid!: instanceId | null
    shade: number = 0  // shade density 0-100+
    declare game: Game

    constructor(data: { position: Position, itemUid: instanceId | null, shade?: number }, game: Game) {
        Object.defineProperty(this, 'game', { get: () => game, set: () => { }, configurable: true })
        Object.assign(this, data)
        return reactive(this) as any
    }

    static from(saved: any, game: Game): Tile {
        return new Tile(saved, game)
    }

    static key({ x, y }: Position): tileKey {
        return `${x},${y}`
    }

    get key(): tileKey {
        return Tile.key(this.position)
    }

    get item(): CompleteItemState | null {
        if (!this.itemUid) return null
        const item = this.game.state.items.get(this.itemUid)
        if (!item) {
            // Item was deleted but tile still has stale UID - clean it up
            this.itemUid = null
            return null
        }
        return item
    }

    set item(value: CompleteItemState | null) {
        this.itemUid = value?.uid ?? null
    }
}

type GameHandlerFunction<K extends keyof GameEvents> = (
    this: Game,
    detail: GameEvents[K]
) => void

type GameHandlersMap = {
    [K in keyof GameEvents]?: GameHandlerFunction<K>
}

export class Game extends TypedEventEmitter<GameEvents, ScheduledEvents> {
    data: CompleteGameData
    state: CompleteGameState
    _lastUid = 0

    override get scheduledEvents(): {
        event: keyof ScheduledEvents,
        atTick: number,
        detail: ScheduledEvents[keyof ScheduledEvents]
    }[] {
        return this.state.scheduledEvents
    }

    constructor(
        data: CompleteGameData,
        state: CompleteGameState
    ) {
        super()
        this.data = data
        this.state = reactive(state)
        this.registerHandlers()
    }

    // #region helpers

    floodConnected(from: Tile, itemType: itemId, excludeTile?: Tile): Tile[] {
        return floodFill(
            [from],
            tile => this.getAdjacentTiles(tile.position),
            tile => tile.key !== excludeTile?.key && this.isValidMergePosition(tile.position, itemType)
        )
    }

    findPositionsFor(
        center: Position,
        count: number,
        range: number,
        valid: (pos: Position) => boolean,
    ): Position[] {
        const positions: Position[] = []
        for (const delta of deltas) {
            if (delta.d2 > range * range) break
            const pos = { x: center.x + delta.x, y: center.y + delta.y }
            if (positions.length >= count) break
            if (valid(pos)) {
                positions.push(pos)
            }
        }
        return positions
    }

    // #endregion helpers

    isValidSpawnPosition(pos: Position): boolean {
        const tile = this.getTileAt(pos)
        return !!tile && !tile.item && tile.shade === 0
    }

    isValidMergePosition(pos: Position, itemId: itemId): boolean {
        const tile = this.getTileAt(pos)
        return !!tile && tile.item?.id === itemId && !tile.item.animation && tile.shade === 0
    }

    // isValidDropPosition(pos: Position, item: Item): boolean {
    //     const tile = this.getTileAt(pos)
    //     if (!tile) return false

    //     return !!tile && !tile.item && tile.shade === 0
    // }

    findSpawnPositions(center: Position, count: number, maxRange?: number): Position[] {
        return this.findPositionsFor(center, count, maxRange ?? 15, this.isValidSpawnPosition.bind(this))
    }

    /**
     * Spawn items from drops at given positions with animation
     * @param drops - What to spawn (item ID -> count)
     * @param center - Animation origin position
     * @param positions - Where to spawn (pre-calculated empty positions)
     * @param chainable - Check for merges after spawn animation completes
     */
    spawnDrops(
        center: Position,
        spawn: {itemId: itemId, position: Position}[],
        chainable: boolean
    ): CompleteItemState[] {
        const ticks = this.data.animations['merge:spread'].duration
        const endTick = this.state.tick + ticks

        const spawnedItems = spawn.map(({itemId, position}) => this.createItem(itemId, {
            position,
            animation: {
                type: 'merge:spread',
                endTick,
                from: center,
                to: position
            }
        }))

        if (chainable) {
            this.schedule('merge:check-chain', endTick, {
                spawnedItemUids: spawnedItems.map(i => i.uid),
                center
            })
        }

        return spawnedItems
    }

    calculateDropMerge(dropTile: Tile, dropItem: CompleteItemState | null) {
        dropItem ??= dropTile.item
        if (!dropItem) return null
        if (dropTile.item) {
            // Item on tile has to be swappable
            if (!this.isValidMergePosition(dropTile.position, dropTile.item.id)) return null
        }

        const connectedTiles = this.floodConnected(dropTile, dropItem.id)
        const connectedItems = unique(
            [dropItem, ...connectedTiles.map(t => t.item)]
            .filter(Boolean)
        )
        
        const drops = calculateMergeDrops(connectedItems.length, dropItem.data)
        if (!drops) return null

        const center = getWeightedCenter(connectedTiles, dropTile)

        return { 
            dropTile, dropItem,
            connectedTiles, connectedItems,
            center, drops
        }
    }

    tryMerge(tile: Tile, item: CompleteItemState | null, center?: Position): boolean {
        const merge = this.calculateDropMerge(tile, item)
        if (!merge) return false

        // Use provided center for chain merges, or calculated center for new merges
        if (center) {
            merge.center = center
        }

        this.emit('merge:start', merge)
        return true
    }

    createItem(
        id: itemId,
        item: Partial<Omit<CompleteItemState, 'id' | 'uid' | 'data'>> = {}
    ): CompleteItemState {
        const data = this.data.items[id]
        if (!data) {
            throw new Error(`Item data not found for id: ${id}`)
        }

        const uid = `${id}#${++this._lastUid}` as instanceId

        const reactiveState = new Item({
            id,
            uid,
            data,
            ...item
        })
        
        // Add item to state first, before linking to tile (to avoid race condition)
        this.state.items.set(uid, reactiveState)
        
        // If item has position, link it to the tile
        if (item.position) {
            const tile = this.getTileAt(item.position)
            if (tile) {
                tile.item = reactiveState
            }
        }
        
        return reactiveState
    }

    getTileAt(pos: Position, flags: 'expect'): Tile;
    getTileAt(pos: Position): Tile | null;
    getTileAt(pos: Position, flags?: 'expect'): Tile | null {
        const tile = this.state.tiles.get(Tile.key(pos)) ?? null
        if (flags === 'expect' && !tile) {
            throw new Error(`Tile not found at position: ${pos.x},${pos.y}`)
        }
        return this.state.tiles.get(Tile.key(pos)) ?? null
    }
        
    getAdjacentTiles(pos: Position): Tile[] {
        const offsets = [
            { x: -1, y: 0 },  // left
            { x: 1, y: 0 },   // right
            { x: 0, y: -1 },  // up
            { x: 0, y: 1 },   // down
        ]
        return offsets
            .map(o => this.getTileAt({ x: pos.x + o.x, y: pos.y + o.y }))
            .filter(Boolean)
    }

    initializeGrid(width: number, height: number): void {
        this.state.size = { x: width, y: height }
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const tile = new Tile({
                    position: { x, y },
                    itemUid: null,
                }, this)
                this.state.tiles.set(tile.key, tile)
            }
        }
    }

    tick(): void {
        // Increment tick and emit event (handler will update state and process scheduled events)
        const tick = this.state.tick + 1
        this.emit('tick', { tick })
    }

    /** Serialize game state to JSON-compatible object */
    saveState() {
        return {
            ...this.state,
            items: Array.from(this.state.items.values()),
            tiles: Array.from(this.state.tiles.values()),
        }
    }

    /** Load game state from serialized data */
    loadState(saved: ReturnType<typeof this.saveState>) {
        Object.assign(this.state, {
            ...saved,
            items: new Map(saved.items.map(data => {
                const item = Item.from(data, this)
                return [item.uid, item]
            })),
            tiles: new Map(saved.tiles.map(data => {
                const tile = Tile.from(data, this)
                return [tile.key, tile]
            })),
        })
    }
}